import { FC, ReactNode } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, PopoverDialog, usePopoverDialog, Text, TextLink, Button } from '@frontend/design-system';

interface FilterChipPopoverProps {
  children: ReactNode;
  value: string;
  onClear: () => void;
  onApply: () => void;
  isClearDisabled: boolean;
  isApplyDisabled: boolean;
  shouldCloseOnClear?: boolean;
  trackingId?: string;
}

export const FilterChipPopover: FC<FilterChipPopoverProps> = ({
  children,
  value,
  onClear,
  onApply,
  isClearDisabled,
  isApplyDisabled,
  shouldCloseOnClear,
  trackingId,
}) => {
  const { t } = useTranslation('team-chat');

  const { getTriggerProps, getDialogProps, close, open, isOpen } = usePopoverDialog({
    placement: 'bottom',
    middlewareOptions: { offset: 8 },
  });

  const onClickClear = () => {
    onClear();
    if (shouldCloseOnClear) {
      close();
    }
  };

  const onClickApply = () => {
    onApply();
    close();
  };

  return (
    <>
      <Chip.DropdownFilter
        trackingId={trackingId}
        onClick={isOpen ? close : open}
        className={'dropdownFilterChipField'}
        css={styles.dropdownChip(isOpen)}
        {...getTriggerProps()}
      >
        <Text as='span' className='label-prefix'>
          {value}
        </Text>
      </Chip.DropdownFilter>
      <PopoverDialog {...getDialogProps()} css={styles.dialog}>
        <TextLink disabled={isClearDisabled} css={styles.clearButton} onClick={onClickClear}>
          {t('Clear')}
        </TextLink>
        {children}
        <Button onClick={onClickApply} disabled={isApplyDisabled}>
          {t('Apply')}
        </Button>
      </PopoverDialog>
    </>
  );
};

const styles = {
  dropdownChip: (isOpen: boolean) => ({
    justifyContent: 'space-around',
    svg: {
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
      height: theme.spacing(1),
      width: theme.spacing(1),
    },
  }),
  dialog: css({
    width: '300px',
  }),

  clearButton: css({
    fontWeight: theme.font.weight.bold,
    cursor: 'pointer',
    alignSelf: 'flex-end',
    marginBottom: theme.spacing(1),
  }),
};
